import { useSelector } from "react-redux";
import { ChartModel } from "./Charts/DynamicChartRender";
import { getDefaultLayoutCase, getLayoutCase, LayoutCase, TilePlacement } from "./Charts/layoutRegistry";
import { RootState } from "src/store";
import { useMemo } from "react";
import { buildMetadataItems, getWeightLabel, parseSrc, parseStatMessageMarkers, resolveStatMessage } from "./Charts/components/chartFootnoteUtils";
import { hasLowNSize } from "./Charts/BarCharts/chartHelpers";

interface TileCell {
	row: number;
	col: number;
}

interface ParsedChart {
	chart: ChartModel;
	cells: TileCell[];
	colStart: number; // 1-based
	colEnd: number; // 1-based inclusive
	spanCols: number;
	spanRows: number;
	startRow: number; // 1-based
}

interface RowGroup {
	rowIndex: number; // 1-based
	cols: number; // number of distinct columns in this row
	tileWidth: number; // pixel width of a single 1×1 tile in this row
	charts: ParsedChart[]; // charts whose startRow === rowIndex
}

interface CSSGridPlacement {
	gridRow: string;
	gridColumn: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse "1,2" or "1,1|1,2" into an array of {row, col} cells.
 */
const parseCells = (positionCells: string): TileCell[] => {
	try {
		return positionCells.split("|").map((seg) => {
			const [r, c] = seg.trim().split(",").map(Number);
			if (isNaN(r) || isNaN(c)) throw new Error();
			return { row: r, col: c };
		});
	} catch {
		return [];
	}
};

const hasOccupancyConflict = (allCells: TileCell[][]): boolean => {
	const seen = new Set<string>();
	for (const cells of allCells) {
		for (const { row, col } of cells) {
			const key = `${row},${col}`;
			if (seen.has(key)) return true;
			seen.add(key);
		}
	}
	return false;
};

const buildFallbackCells = (count: number): TileCell[][] => {
	const cols = count <= 3 ? count : count === 4 ? 2 : 3;
	return Array.from({ length: count }, (_, i) => [{ row: Math.floor(i / cols) + 1, col: (i % cols) + 1 }]);
};

// ─────────────────────────────────────────────────────────────────────────────
// Output type
// ─────────────────────────────────────────────────────────────────────────────

export interface ResolvedTile {
	chart: ChartModel;
	placement: TilePlacement;
	/** Actual pixel width of this tile (accounts for gaps) */
	tileWidthPx: number;
	/** Actual pixel height of this tile (accounts for gaps) */
	tileHeightPx: number;
}

export interface ResolvedLayout {
	/** The resolved LayoutCase (may be fallback) */
	layoutCase: LayoutCase;
	/** Per-chart resolved info, in chart-array order */
	tiles: ResolvedTile[];
	/** True if the requested case was invalid and we fell back to default */
	fallbackApplied: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves a layout_case string into concrete pixel dimensions for each tile.
 *
 * @param charts       — array of ChartModel (only length + identity needed here)
 * @param layoutCase   — backend-supplied case ID, e.g. "case1", "case3"
 * @param wrapperWidth — measured pixel width of the grid container
 * @param wrapperHeight— measured pixel height of the grid container
 * @param rowGap       — pixel gap between grid rows
 * @param columnGap    — pixel gap between grid columns
 */
export function computeLayoutFromCase(
	charts: ChartModel[],
	layoutCase: string | undefined | null,
	wrapperWidth: number,
	wrapperHeight: number,
	rowGap: number,
	columnGap: number,
): ResolvedLayout {
	const count = charts.length;
	// 1. Resolve the LayoutCase — try exact match, then default, then synthesize
	let resolved = layoutCase ? getLayoutCase(count, layoutCase) : null;
	let fallbackApplied = false;

	if (!resolved) {
		resolved = getDefaultLayoutCase(count);
		fallbackApplied = !!layoutCase; // only flag fallback if a case was actually sent
	}

	// Ultimate fallback for unsupported chart counts: single column stack
	if (!resolved) {
		resolved = buildSingleColumnFallback(count);
		fallbackApplied = true;
	}

	// 2. Compute available space after gaps
	const totalHGap = columnGap * (resolved.colCount - 1);
	const totalVGap = rowGap * (resolved.rowCount - 1);
	const availableWidth = Math.max(0, wrapperWidth - totalHGap);
	const availableHeight = Math.max(0, wrapperHeight - totalVGap);

	// 3. Map each chart to its tile with pixel dimensions
	const tiles: ResolvedTile[] = charts.map((chart, i) => {
		const placement = resolved!.tiles[i];

		// Guard: more charts than placements → give them zero-size (won't render)
		if (!placement) {
			return {
				chart,
				placement: { gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0, heightFraction: 0 },
				tileWidthPx: 0,
				tileHeightPx: 0,
			};
		}

		// Pixel dimensions based on fraction of available (gap-subtracted) space.
		// For spanning tiles the fraction already accounts for the span
		// (e.g. a full-width tile in a 3-col grid has widthFraction = 1.0).
		// We add back the gaps that fall *within* the span.
		const spanCols = countSpan(placement.gridColumn);
		const spanRows = countSpan(placement.gridRow);
		const internalHGap = columnGap * (spanCols - 1);
		const internalVGap = rowGap * (spanRows - 1);

		const tileWidthPx = availableWidth * placement.widthFraction + internalHGap;
		const tileHeightPx = availableHeight * placement.heightFraction + internalVGap;

		return { chart, placement, tileWidthPx, tileHeightPx };
	});

	return { layoutCase: resolved, tiles, fallbackApplied };
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse "1 / 3" → span of 2 (3 - 1).
 */
function countSpan(gridLine: string): number {
	const parts = gridLine.split("/").map((s) => parseInt(s.trim(), 10));
	if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
		return Math.max(1, parts[1] - parts[0]);
	}
	return 1;
}

/**
 * Generates a simple N-row × 1-col stack for unsupported chart counts.
 */
function buildSingleColumnFallback(count: number): LayoutCase {
	const hFrac = 1 / count;
	return {
		gridTemplateColumns: "1fr",
		gridTemplateRows: Array(count).fill("1fr").join(" "),
		rowCount: count,
		colCount: 1,
		tiles: Array.from({ length: count }, (_, i) => ({
			gridRow: `${i + 1} / ${i + 2}`,
			gridColumn: "1 / 2",
			widthFraction: 1.0,
			heightFraction: hFrac,
		})),
	};
}
/** First global Case number for each chart-count bucket */
const BUCKET_START: Record<number, number> = {
	2: 1,
	3: 5,
	4: 12,
	5: 26,
	6: 33,
};

/**
 * Extract the numeric part from "Case_6" → 6
 */
// Safe: static literal pattern; anchored `^case\d+$` has no backtracking risk.
const CASE_FORMAT_REGEX = /^case\d+$/i;

function parseGlobalCaseNumber(viewLayout: string): number | null {
	const match = viewLayout.match(/^Case_(\d+)$/i);
	return match ? parseInt(match[1], 10) : null;
}

/**
 * Converts a backend `view_layout` string to the registry `caseId`.
 *
 * @param viewLayout   — e.g. "Case_6"
 * @param chartCount   — number of charts (from view_chart_count or array length)
 * @returns            — e.g. "case2", or "case1" as fallback
 *
 * @example
 *   normalizeViewLayout("Case_6", 3)  → "case2"
 *   normalizeViewLayout("Case_12", 4) → "case1"
 *   normalizeViewLayout("Case_33", 6) → "case1"
 */
export function normalizeViewLayout(viewLayout: string | undefined | null, chartCount: number): string {
	if (!viewLayout) return "case1";

	const globalNum = parseGlobalCaseNumber(viewLayout);
	if (globalNum === null) {
		// Maybe it's already in registry format ("case3")
		if (CASE_FORMAT_REGEX.test(viewLayout)) {
			return viewLayout.toLowerCase();
		}
		return "case1";
	}

	const start = BUCKET_START[chartCount];
	if (start === undefined) return "case1";

	const localIndex = globalNum - start + 1;
	if (localIndex < 1) return "case1";

	return `case${localIndex}`;
}

/**
 * Infer chart count from the global case number alone (without view_chart_count).
 * Useful as a fallback if view_chart_count is missing.
 */
export function inferChartCountFromCase(viewLayout: string): number | null {
	const globalNum = parseGlobalCaseNumber(viewLayout);
	if (globalNum === null) return null;

	if (globalNum >= 33) return 6;
	if (globalNum >= 26) return 5;
	if (globalNum >= 12) return 4;
	if (globalNum >= 5) return 3;
	if (globalNum >= 1) return 2;
	return null;
}

export interface ChartInfoData {
	title: string;
	subtitle?: string;
	quesNo?: string;
	questionText?: string;
	metadataItems: React.ReactNode[];
}

export function useChartInfoData(chartResponse: any): ChartInfoData {
	const { selectedStudy } = useSelector((state: RootState) => state.studiesSlice);

	const title = chartResponse?.chart_title ?? "";
	const subtitle = chartResponse?.chart_subtitle;
	const src: string | undefined = chartResponse?.footnote?.src;
	const isWeighted: boolean | undefined = chartResponse?.is_weight;
	const analysisName: string | undefined = chartResponse?.analysis_type;
	const statMessageRaw: string | undefined = chartResponse?.footnote?.stat_message;
	const apiData = chartResponse?.data?.[0]?.chart_data;

	/* ── Derived values (all memoised) ──────────────────────── */

	const { quesNo, questionText } = useMemo(() => parseSrc(src), [src]);

	const showLowNSize = useMemo(() => hasLowNSize(apiData, { threshold: 30 }), [apiData]);

	const weightLabel = useMemo(() => getWeightLabel(isWeighted), [isWeighted]);

	const resolvedStatMsg = useMemo(() => resolveStatMessage(statMessageRaw), [statMessageRaw]);

	const parsedMarkers = useMemo(() => parseStatMessageMarkers(resolvedStatMsg ?? ""), [resolvedStatMsg]);

	const metadataItems = useMemo(
		() =>
			buildMetadataItems({
				hasLowNSize: showLowNSize,
				analysisName,
				weightLabel,
				parsedStatMarkers: parsedMarkers,
				resolvedStatMessage: resolvedStatMsg,
				studyName: selectedStudy?.study_name,
			}),
		[showLowNSize, analysisName, weightLabel, parsedMarkers, resolvedStatMsg, selectedStudy?.study_name],
	);

	return { title, subtitle, quesNo, questionText, metadataItems };
}
